import { z } from 'zod'

import { http } from '@/shared/api'

import { NETWORKS, PURCHASE_SCENARIOS } from '../model/types'
import type { CreatedInvoice, DurationDays, Invoice, Network, Plan } from '../model/types'

/* ── Plans ─────────────────────────────────────────────────────────────── */

const priceSchema = z.object({
  duration_days: z.number(),
  amount_cents: z.number(),
  scenario: z.enum(PURCHASE_SCENARIOS),
  bonus_days: z.number(),
  period_end: z.string(),
  starts_at: z.string().optional(),
})

const planSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name_ru: z.string(),
  name_en: z.string(),
  tier: z.number(),
  max_campaigns: z.number().nullable(),
  is_popular: z.boolean(),
  prices: z.array(priceSchema).nullable(),
  unavailable: z.boolean(),
  unavailable_reason: z.string().optional(),
})

const plansSchema = z.object({ plans: z.array(planSchema).nullable() })

const toPlan = (raw: z.infer<typeof planSchema>): Plan => ({
  id: raw.id,
  slug: raw.slug,
  nameRu: raw.name_ru,
  nameEn: raw.name_en,
  tier: raw.tier,
  maxCampaigns: raw.max_campaigns,
  isPopular: raw.is_popular,
  prices: (raw.prices ?? []).map((price) => ({
    durationDays: price.duration_days,
    amountCents: price.amount_cents,
    scenario: price.scenario,
    bonusDays: price.bonus_days,
    periodEnd: price.period_end,
    startsAt: price.starts_at,
  })),
  isUnavailable: raw.unavailable,
  unavailableReason: raw.unavailable_reason ?? '',
})

export async function fetchPlans(signal?: AbortSignal): Promise<Plan[]> {
  const { plans } = await http.get('plans', { schema: plansSchema, signal })
  return (plans ?? []).map(toPlan).sort((a, b) => a.tier - b.tier)
}

/* ── Invoices ──────────────────────────────────────────────────────────── */

const networkSchema = z.enum(NETWORKS)

const invoiceSchema = z.object({
  id: z.string(),
  amount_cents: z.number(),
  network: networkSchema,
  symbol: z.string(),
  decimals: z.number(),
  pay_address: z.string(),
  // Строка, а не число: 18 знаков BSC не переживают JSON-число.
  pay_amount_units: z.string(),
  status: z.string(),
  confirmations: z.number(),
  expires_at: z.string(),
  confirmed_at: z.string().optional(),
  created_at: z.string(),
})

const invoicesSchema = z.object({ items: z.array(invoiceSchema).nullable() })

const toInvoice = (raw: z.infer<typeof invoiceSchema>): Invoice => ({
  id: raw.id,
  amountCents: raw.amount_cents,
  network: raw.network,
  symbol: raw.symbol,
  decimals: raw.decimals,
  payAddress: raw.pay_address,
  payAmountUnits: raw.pay_amount_units,
  status: raw.status,
  confirmations: raw.confirmations,
  expiresAt: raw.expires_at,
  confirmedAt: raw.confirmed_at ?? '',
  createdAt: raw.created_at,
})

export async function fetchInvoices(signal?: AbortSignal): Promise<Invoice[]> {
  const { items } = await http.get('invoices', { schema: invoicesSchema, signal })
  return (items ?? []).map(toInvoice)
}

const createdSchema = z.object({
  invoice_id: z.string(),
  amount_cents: z.number(),
  network: networkSchema,
  symbol: z.string(),
  decimals: z.number(),
  pay_address: z.string(),
  pay_amount_units: z.string(),
  status: z.string(),
  expires_at: z.string(),
})

export async function createInvoice(input: {
  planId: string
  durationDays: DurationDays
  network: Network
}): Promise<CreatedInvoice> {
  const raw = await http.post('invoices', {
    json: {
      plan_id: input.planId,
      duration_days: input.durationDays,
      network: input.network,
    },
    schema: createdSchema,
  })

  return {
    invoiceId: raw.invoice_id,
    amountCents: raw.amount_cents,
    network: raw.network,
    symbol: raw.symbol,
    decimals: raw.decimals,
    payAddress: raw.pay_address,
    payAmountUnits: raw.pay_amount_units,
    status: raw.status,
    expiresAt: raw.expires_at,
  }
}
